import * as React from 'react';
import * as PropTypes from 'prop-types';
import {
  SelectionZone,
  ISelectionZoneProps,
  MarqueeSelection,
  ISelection,
  SelectionMode
} from 'office-ui-fabric-react/lib/MarqueeSelection';
import {
  autobind
} from 'office-ui-fabric-react/lib/Utilities';
import {
  TilesList,
  ITilesListProps,
  ITilesGridSegment,
  ITilesGridItem,
  ITileSize,
  TilesGridMode
} from '@uifabric/experiments/lib/TilesList';
import { ITileProps } from '@uifabric/experiments/lib/Tile';
import { provideContext, connectToContext } from '../../utilities/components/Context';
import { connectToItemCache } from '../decorators/connectToItemCache';
import { IItemCache } from '../../models/IItemCacheObserver';
import { ItemTile, IItemTileMode } from '../itemTile/ItemTile';
import {
  withLayoutRenderComplete, ILayoutRenderProps, IDecorateWithLayoutRenderCompleteProps
} from '../decorators/withLayoutRenderComplete';
import { ItemPage } from '../itemPage/ItemPage';
import * as ItemSetTilesListStyles from './ItemSetTilesList.scss';
import { childItemsControl } from '../../controls/childItemsControl';
import { ShimmerTile } from '@uifabric/experiments/lib/components/Shimmer/ShimmerTile/ShimmerTile';
import { ItemDefaultDropZone } from '../defaultDropZone/DefaultDropZone';
import { IActionDropZoneStateProps } from '../decorators/asActionDropZone';
import { childItemsFacet, pageFacet } from '../../facets/SetFacets';
import { isGroupFacet, mediaGroupFacet } from '../../facets/GroupFacets';
import { errorFacet } from '../../facets/DemandFacets';
import { displayNameFacet } from '../../facets/ItemFacets';
import { IActionStateProps, WithAction } from '../withAction/WithAction';
import { IDecoratedProps, withDisplayName } from '../../utilities/components/Decorators';
import { withResponsiveMode } from '../decorators/withResponsiveMode';
import { ResponsiveMode } from 'office-ui-fabric-react/lib/utilities/decorators/withResponsiveMode';
import {
  itemSetTilesListControl,
  itemSetTilesListGroupControl,
  itemSetTilesListTileControl,
  IItemTileCellProps,
  IHeaderTileCellProps
} from '../../controls/itemSetTilesListControl';
import { ISpecifiedItemProps } from '../decorators/selectors';
import { ItemDefaultClickAction } from '../actions/defaultClick/DefaultClickAction';
import { RegisterInvokeAction } from '../decorators/withActionInvoker';
import { thumbnailFacet, dimensionsFacet } from '../../facets/MediaFacets';
import { pickObject } from '../../utilities/objects';

const SIZES: {
  [P in TilesListSize]: {
    tileDefaultHeight: number;
    tileDefaultWidth: number;
    documentGroupTileMargin: number;
    photoGroupTileMargin: number;
    groupMargin: number;
  };
} = {
  small: {
    tileDefaultHeight: 135,
    tileDefaultWidth: 138,
    documentGroupTileMargin: 12,
    photoGroupTileMargin: 8,
    groupMargin: 40
  },
  large: {
    tileDefaultHeight: 171,
    tileDefaultWidth: 176,
    documentGroupTileMargin: 8,
    photoGroupTileMargin: 8,
    groupMargin: 40
  }
};

export { IItemTileCellProps };

const SelectionContext = provideContext({
  selection: PropTypes.object
}, (props: {
  selection?: ISelection;
}) => {
    return {
      selection: props.selection
    };
  });

const SelectableItemTile = connectToContext({
  selection: PropTypes.object
}, (context: {
  selection?: ISelection;
}): Pick<ITileProps, 'selection'> => {
    return {
      selection: context.selection
    };
  })(ItemTile);

class ItemTileCell extends React.PureComponent<IItemTileCellProps> {
  public render(): JSX.Element {
    const {
      props
    } = this;

    const {
      itemKey
    } = props;

    return (
      // Render the action used to open the item, so that it bubbles up its availability and execute function
      // to the action dispatcher.
      <WithAction action={ <ItemDefaultClickAction itemKey={ itemKey } /> }>
        { (invokeActionState: IActionStateProps) => {
          const {
            isAvailable
          } = invokeActionState;

          return (
            <ItemDefaultDropZone itemKey={ itemKey } layoutMode='absolute'>
              { ({ isDropping }: IActionDropZoneStateProps) => (
                <>
                  <RegisterInvokeAction
                    { ...invokeActionState }
                    itemKey={ itemKey }
                    index={ props.selectionIndex }
                  />
                  <SelectableItemTile
                    contentSize={ props.contentSize }
                    itemKey={ itemKey }
                    tileSize={ props.tileSize }
                    selectionIndex={ props.selectionIndex }
                    invokeSelection={ isAvailable }
                    href={ isAvailable ? 'javascript:;' : undefined }
                    { ...props }
                  />
                </>
              ) }
            </ItemDefaultDropZone>
          );
        } }
      </WithAction>
    );
  }
}

const HeaderTileCell: React.ComponentType<ISpecifiedItemProps> = connectToItemCache((itemCache: IItemCache, props: ISpecifiedItemProps) => {
  const displayName = itemCache.demandItemFacet(displayNameFacet, props.itemKey);

  return {
    text: displayName || ''
  };
})((props: {
  text: string;
}) => {
  return (
    <div>
      <h3>{ props.text }</h3>
    </div>
  );
});

/**
 * @public
 */
export interface IItemSetTilesListCellContent {
  key: string;
  index: number;
  count: number;
  tileSize: ITileProps['tileSize'];
  tileMode: IItemTileMode;
  tileRenderer?: React.ComponentType<IItemTileCellProps>;
  headerRenderer?: React.ComponentType<IHeaderTileCellProps>;
}

type TilesListSize = 'small' | 'large';

/**
 * @public
 */
export interface IDefaultTilesListProps extends ITilesListProps<IItemSetTilesListCellContent> {
  selection?: ISelection;
  selectionItems: {
    key: string;
  }[];
  selectionMode?: SelectionMode;
  onItemInvoked?: ISelectionZoneProps['onItemInvoked'];
  onItemContextMenu?: ISelectionZoneProps['onItemContextMenu'];
  setKey: string;
  disableMarqueeSelection?: boolean;
  ariaLabel?: string;
}

declare class ItemTilesListClass extends TilesList<IItemSetTilesListCellContent> { }

const ItemTilesList: typeof ItemTilesListClass = TilesList;

class DefaultTilesList extends React.PureComponent<IDefaultTilesListProps, {}> {
  constructor(props: IDefaultTilesListProps) {
    super(props);

    const {
      selection,
      selectionItems
    } = this.props;

    if (selection) {
      selection.setItems(selectionItems, true);
    }
  }

  public render(): JSX.Element {
    const {
      selection,
      selectionMode,
      ref,
      disableMarqueeSelection,
      ariaLabel,
      ...tilesListProps
    } = this.props;

    const tilesList = (
      <ItemTilesList
        role='presentation'
        className={ ItemSetTilesListStyles.root }
        aria-label={ ariaLabel }
        { ...tilesListProps }
      />
    );

    const tilesListWithSelection = selection ? (
      <SelectionZone
        selection={ selection }
        selectionMode={ selectionMode }
        onItemInvoked={ this.props.onItemInvoked }
        onItemContextMenu={ this.props.onItemContextMenu }
        disableAutoSelectOnInputElements={ true }
      >
        <SelectionContext selection={ selection }>
          { tilesList }
        </SelectionContext>
      </SelectionZone>
    ) : tilesList;

    return selection ?
      (
        disableMarqueeSelection ?
          (
            tilesListWithSelection
          ) :
          (
            <MarqueeSelection
              selection={ selection }
              onShouldStartSelection={ this._onShouldStartSelection }
            >
              { tilesListWithSelection }
            </MarqueeSelection>
          )
      ) : (
        tilesList
      );
  }

  public componentWillReceiveProps(nextProps: IDefaultTilesListProps): void {
    const {
      selection,
      selectionItems
    } = nextProps;

    if (selection) {
      selection.setItems(selectionItems, nextProps.setKey !== this.props.setKey || !nextProps.setKey);
    }
  }

  @autobind
  private _onShouldStartSelection(event: MouseEvent): boolean {
    let currentElement: HTMLElement | null = event.target as HTMLElement;

    while (currentElement && currentElement !== document.body) {
      const selectionIndex = currentElement.getAttribute('data-selection-index');

      if (selectionIndex !== null) {
        return false;
      }

      currentElement = currentElement.parentElement;
    }

    return true;
  }
}

const NormalizedTilesList = withLayoutRenderComplete()(DefaultTilesList);

/**
 * @public
 */
export type IItemSetTilesListProps = IDecoratedProps<IDecorateWithLayoutRenderCompleteProps<IDefaultTilesListProps>,
  Pick<IDefaultTilesListProps, 'items' | 'selectionItems' | 'setKey'> &
  Pick<ILayoutRenderProps, 'isDataReadyForLayout'>,
  {
    itemKey?: string | undefined;
    doNotRenderShimmerTile?: boolean | undefined;
    responsiveMode?: ResponsiveMode | undefined;
  }>;

/**
 * @public
 */
export const ItemSetTilesList: React.ComponentType<IItemSetTilesListProps> =
  withDisplayName('ItemSetTilesList')(withResponsiveMode()(connectToItemCache((itemCache: IItemCache, props: {
    itemKey?: string | undefined;
    doNotRenderShimmerTile?: boolean | undefined;
    responsiveMode?: ResponsiveMode | undefined;
  }): Pick<IDefaultTilesListProps, 'items' | 'selectionItems' | 'setKey'> &
    Pick<ILayoutRenderProps, 'isDataReadyForLayout'> => {
    const {
      itemKey,
      doNotRenderShimmerTile = false,
      responsiveMode = ResponsiveMode.large
    } = props;

    const childItems = itemCache.demandItemFacet(childItemsFacet, itemKey);
    const isMediaSet = itemCache.demandItemFacet(mediaGroupFacet, itemKey);

    const childItemsControlHandler = itemCache.demandItemFacet(childItemsControl, itemKey);

    const {
      itemKeys
    } = childItemsControl.evaluate(childItemsControlHandler)(itemCache, {
      itemKey: itemKey,
      itemKeys: childItems && childItems.itemKeys
    });

    const tileListSize: ITileProps['tileSize'] = responsiveMode > ResponsiveMode.large ? 'large' : 'small';
    const sizes = SIZES[tileListSize];

    const defaultDesiredSize = getDocumentTileDesiredSize(tileListSize);

    const tilesListControlHandler = itemCache.demandItemFacet(itemSetTilesListControl, itemKey);

    const mediaGrid = {
      mode: TilesGridMode.fill,
      spacing: sizes.photoGroupTileMargin
    };

    const defaultGrid = {
      mode: tileListSize === 'large' ?
        TilesGridMode.stack :
        TilesGridMode.fillHorizontal,
      marginBottom: sizes.groupMargin,
      spacing: sizes.documentGroupTileMargin,
      minRowHeight: sizes.tileDefaultHeight
    };

    const {
      tileRenderer: TileRenderer,
      groupControlHandler,
      tileControlHandler,
      grid
    } = itemSetTilesListControl.evaluate(tilesListControlHandler)(itemCache, {
      itemSetKey: itemKey,
      itemKeys,
      tileRenderer: ItemTileCell,
      grid: {
        ...defaultGrid,
        ...(isMediaSet ? mediaGrid : {})
      }
    });

    const items: (ITilesGridItem<IItemSetTilesListCellContent> | ITilesGridSegment<IItemSetTilesListCellContent>)[] = [];

    const selectionItems: {
      key: string;
    }[] = [];

    let defaultGridItems: ITilesGridItem<IItemSetTilesListCellContent>[] = [];

    let index = 0;

    if (itemKeys) {
      for (const childItemKey of itemKeys) {
        if (itemCache.isItemFacetResolved(pageFacet, childItemKey)) {
          defaultGridItems.push({
            key: childItemKey,
            content: {
              key: childItemKey,
              count: length, // TODO this needs to be the total number of items.
              index: -1,
              tileSize: tileListSize,
              tileMode: 'document',
            },
            onRender: renderPage
          });
        } else if (itemCache.isItemFacetResolved(isGroupFacet, childItemKey) && itemCache.demandItemFacet(isGroupFacet, childItemKey)) {
          if (defaultGridItems.length > 0) {
            items.push({
              key: '',
              items: defaultGridItems,
              ...grid
            });
            defaultGridItems = [];
          }

          const groupChildItemKeys = getFlattenedGroupChildItemKeys(itemCache, { itemKey: childItemKey });
          const isMediaGroup = itemCache.demandItemFacet(mediaGroupFacet, childItemKey) || isMediaSet;

          const {
            tileRenderer: GroupTileRenderer,
            headerRenderer: GroupHeaderRenderer,
            tileControlHandler: groupTileControlHandler,
            grid: groupGrid
          } = itemSetTilesListGroupControl.evaluate(groupControlHandler)(itemCache, {
            groupKey: childItemKey,
            itemKeys: groupChildItemKeys,
            tileRenderer: TileRenderer,
            headerRenderer: HeaderTileCell,
            grid: {
              ...defaultGrid,
              ...(isMediaGroup ? mediaGrid : {})
            }
          });

          if (GroupHeaderRenderer) {
            items.push({
              key: childItemKey,
              content: {
                key: childItemKey,
                count: length, // TODO this needs to be the total number of items.
                index: -1,
                tileSize: tileListSize,
                tileMode: 'document',
                headerRenderer: GroupHeaderRenderer
              },
              onRender: renderHeader
            });
          }

          const groupItems: ITilesGridItem<IItemSetTilesListCellContent>[] = [];

          for (const groupChildItemKey of groupChildItemKeys) {
            selectionItems.push({
              key: groupChildItemKey
            });

            const {
              desiredSize,
              tileMode: groupTileMode
            } = itemSetTilesListTileControl.evaluate(groupTileControlHandler)(itemCache, {
              itemKey: groupChildItemKey,
              desiredSize: isMediaGroup && getMediaTileDesiredSize(itemCache, { itemKey: groupChildItemKey }) || defaultDesiredSize,
              tileMode: isMediaGroup ? 'media' : 'document'
            });

            if (itemCache.isItemFacetResolved(pageFacet, groupChildItemKey)) {
              groupItems.push({
                key: childItemKey,
                content: {
                  key: childItemKey,
                  count: length, // TODO this needs to be the total number of items.
                  index: -1,
                  tileSize: tileListSize,
                  tileMode: 'document',
                },
                onRender: renderPage
              });
            } else {
              groupItems.push({
                key: groupChildItemKey,
                content: {
                  key: groupChildItemKey,
                  count: groupChildItemKeys.length,
                  index: index,
                  tileSize: tileListSize,
                  tileMode: groupTileMode,
                  tileRenderer: GroupTileRenderer
                },
                desiredSize,
                onRender: renderItemTile
              });
            }

            index++;
          }

          const groupSegment: ITilesGridSegment<IItemSetTilesListCellContent> = {
            key: childItemKey,
            items: groupItems,
            ...groupGrid
          };

          items.push(groupSegment);
        } else {
          selectionItems.push({
            key: childItemKey
          });

          const {
            desiredSize,
            tileMode
          } = itemSetTilesListTileControl.evaluate(tileControlHandler)(itemCache, {
            itemKey: childItemKey,
            desiredSize: isMediaSet && getMediaTileDesiredSize(itemCache, { itemKey: childItemKey }) || defaultDesiredSize,
            tileMode: isMediaSet ? 'media' : 'document'
          });

          defaultGridItems.push({
            key: childItemKey,
            content: {
              key: childItemKey,
              count: length, // TODO this needs to be the total number of items.
              index: index,
              tileSize: tileListSize,
              tileMode,
              tileRenderer: TileRenderer
            },
            desiredSize,
            onRender: renderItemTile
          });

          index++;
        }
      }

      if (defaultGridItems.length > 0) {
        items.push({
          key: '',
          items: defaultGridItems,
          ...grid
        });
      }
    } else if (!doNotRenderShimmerTile && !itemCache.isItemFacetResolved(errorFacet, itemKey)) {
      items.push({
        key: '',
        items: [{
          key: '',
          content: {
            key: '',
            count: length,
            index: index,
            tileSize: tileListSize,
            tileMode: isMediaSet ? 'media' : 'document'
          },
          desiredSize: getDocumentTileDesiredSize(tileListSize),
          onRender: renderShimmerTile,
          isPlaceholder: true
        }],
        ...grid,
        isPlaceholder: true
      });
    }

    const isDataReadyForLayout =
      itemCache.isItemFacetResolved(childItemsFacet, itemKey) ||
      itemCache.isItemFacetResolved(errorFacet, itemKey);

    return {
      setKey: itemKey || '',
      items: items,
      selectionItems: selectionItems,
      isDataReadyForLayout: isDataReadyForLayout
    };
  })(NormalizedTilesList)));

function renderItemTile(content: IItemSetTilesListCellContent, finalSize: ITileSize | undefined): JSX.Element | null {
  const {
    tileRenderer: Renderer
  } = content;

  return Renderer ? (
    <Renderer
      contentSize={ finalSize }
      itemKey={ content.key }
      tileSize={ content.tileSize }
      itemTileMode={ content.tileMode }
      selectionIndex={ content.index }
    />
  ) : null;
}

function renderHeader(content: IItemSetTilesListCellContent): JSX.Element | null {
  const {
    headerRenderer: Renderer
  } = content;

  return Renderer ? (
    <Renderer itemKey={ content.key } />
  ) : null;
}

function renderPage(content: IItemSetTilesListCellContent): JSX.Element {
  return (
    <ItemPage
      itemKey={ content.key }
    />
  );
}

function renderShimmerTile(content: IItemSetTilesListCellContent, finalSize: ITileSize | undefined): JSX.Element {
  return (
    <ShimmerTile
      contentSize={ finalSize }
      itemName={ true }
      itemActivity={ true }
      itemThumbnail={ true }
    />
  );
}

function getDocumentTileDesiredSize(tilesListSize: TilesListSize): ITileSize {
  const sizes = SIZES[tilesListSize];

  return {
    width: sizes.tileDefaultWidth,
    height: sizes.tileDefaultHeight
  };
}

function getMediaTileDesiredSize(itemCache: IItemCache, props: { itemKey: string; }): ITileSize | undefined {
  const dimensions =
    itemCache.demandItemFacet(thumbnailFacet, props.itemKey) ||
    itemCache.demandItemFacet(dimensionsFacet, props.itemKey);

  return dimensions && pickObject(dimensions, ['width', 'height']);
}

function getFlattenedGroupChildItemKeys(itemCache: IItemCache, props: {
  itemKey: string;
}): string[] {
  const childItems = itemCache.demandItemFacet(childItemsFacet, props.itemKey);
  const childItemKeys = childItems && childItems.itemKeys || [];

  const resultKeys: string[] = [];

  for (const childItemKey of childItemKeys) {
    if (itemCache.isItemFacetResolved(isGroupFacet, childItemKey) && itemCache.demandItemFacet(isGroupFacet, childItemKey)) {
      resultKeys.push(...getFlattenedGroupChildItemKeys(itemCache, { itemKey: childItemKey }));
    } else {
      resultKeys.push(childItemKey);
    }
  }

  return resultKeys;
}
